import { Pipe, PipeTransform } from '@angular/core';

/**
 * Pure check: does the given string look like an encrypted ciphertext?
 *
 * Real cipher outputs (AWS KMS, parse-server crypto) are typically:
 *   - 80+ characters long
 *   - made only of base64-alphabet characters: `A–Z a–z 0–9 + / = _ -`
 *   - never contain spaces, periods, accents, or any natural punctuation
 *
 * Human-readable case names — even very long ones like
 * "Paciente Hospital Universitario - revisión 2024" — always contain
 * spaces or punctuation that fail the alphabet check, so the heuristic
 * doesn't false-positive on them.
 *
 * The 40-char threshold is conservative: shorter alphanumeric strings
 * (codes, IDs, etc.) might be legitimate case names; only the long
 * cipher-looking blobs get masked.
 */
export function isLikelyCipherText(value: string | null | undefined): boolean {
  if (!value || value.length < 40) return false;
  return /^[A-Za-z0-9+/=_-]+$/.test(value);
}

/**
 * `{{ case.name | displayCaseName }}`
 *
 * Returns the name unchanged unless it looks like ciphertext, in which
 * case it returns the standard masked placeholder. Used in views where:
 *   - the workspace MIGHT be encrypted and we don't have a reactive
 *     `displayCaseName$` selector locally (e.g. microscope-image
 *     navigation, where decryption is not in scope), or
 *   - a transient race in the state machine could otherwise expose the
 *     raw cipher for a few hundred ms (e.g. case-analysis breadcrumbs).
 *
 * Use the optional `mask` arg to control what's displayed instead of
 * the default `******`.
 */
@Pipe({
  name: 'displayCaseName',
  standalone: false,
})
export class DisplayCaseNamePipe implements PipeTransform {
  transform(value: string | null | undefined, mask = '******'): string | null {
    if (value == null) return value ?? null;
    return isLikelyCipherText(value) ? mask : value;
  }
}
